<template>
    <div class="main report">

        <div class="report-heading">
            <h1><span class="breadcrumb" @click="clearFilters()">Store Inventory</span><i v-if="department_name" class="fas fa-chevron-right"></i>{{ department_name }}</h1>
            <div v-if="notEmpty(totals)" class="report-total">
                <div>
                    <label>Total On Hand Cost</label><br>{{ formatCurrency(totals.cost_on_hand) }}
                </div>
                <div>
                    <label>Total On Hand Retail</label><br>{{ formatCurrency(totals.price_on_hand) }}
                </div>
                <div>
                    <label>Total Margin</label><br>{{ totals.margin_on_hand }}
                </div>
            </div>
        </div>

        <div class="filters with-compare">
            <div class="three-columns selectors">
                <label>UPC / SKU</label>
                <label v-if="showDealerFilter">Dealer</label>
                <label v-else></label>
                <label></label>
                <div class="input-with-control">
                    <input class="inventory" type="search" ref="search_term" v-model="filters.search_text" placeholder="Search..." @search="setFilter('search_text')">
                    <div class="button inventory input-go" @click="setFilter('search_text')"><i class="fas fa-search"></i></div>
                    <div v-if="itemSearchList.length > 0" class="search-list">Message or list</div>
                </div>
                <div v-if="showDealerFilter" class="input-with-control">
                    <input type="text" ref="search_term" v-model="filters.dealer" placeholder="Search..." @keyup.enter="setFilter('location')">
                    <div class="button inventory input-go" @click="setFilter('location')"><i class="fas fa-search"></i></div>
                    <div v-if="dealerSearchList.length > 0 || dealerSearchMessage" class="search-list">
                        <div v-for="dealer in dealerSearchList" class="search-list-item" :key="dealer.location_id" @click="setFilterFromSearch(location)">{{ dealer.description }}</div>
                        <div v-if="dealerSearchMessage">{{ dealerSearchMessage }}</div>
                    </div>
                </div>
                <div v-else></div>
            </div>
            <div>
                <div class="input-with-control date-control">
                    <label>Start</label>
                    <label>End</label>
                    <label></label>
                    <input class="inventory" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.start_date" required="required">
                    <input class="inventory" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.end_date" required="required">
                    <div class="button inventory input-go" @click="setFilterDates()"><i class="fas fa-calendar-check"></i></div>
                </div>
            </div>
            <div>
                <div class="button inventory compare-button" @click="showCompare = true"><i class="fas fa-calendar-plus"></i></div>
            </div>
        </div>

        <div v-if="message" class="empty-list">
            {{ message }}
            <div v-if="loading" class="loading"><i class="fas fa-spinner fa-spin"></i></div>
        </div>

        <template v-if="!loading">
            <div v-for="(list, section) in lists" :key="section" class="report-list department">
                <div v-if="list.length > 0" class="report-row heading">
                    <div></div>
                    <div></div>
                    <div style="grid-column: 3 / span 5; text-align: center; border-bottom: 1px solid #ccc;">Current Onhand Inventory</div>
                    <div class="dark-shaded"></div>
                    <div style="grid-column: 9 / span 6; text-align: center; border-bottom: 1px solid #ccc;">Sales for Period</div>

                    <h2>{{ sectionTitle(section) }}</h2>
                    <label></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_on_hand' }]" @click="setSort('total_on_hand')">Units<br>On Hand<span v-html="showSort('total_on_hand')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_cost_on_hand' }]" @click="setSort('total_cost_on_hand')">Cost<br>On Hand<span v-html="showSort('total_cost_on_hand')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_price_on_hand' }]" @click="setSort('total_price_on_hand')">Retail<br>On Hand<span v-html="showSort('total_price_on_hand')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_profit' }]" @click="setSort('total_profit')"><br>Profit<span v-html="showSort('total_profit')"></span></label>
                    <label class="right"><br>Margin</label>
                    <div class="right"><br></div>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_sold' }]" @click="setSort('total_sold')">Sold or<br>Disposed<span v-html="showSort('total_sold')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_cost_sold' }]" @click="setSort('total_cost_sold')">Cost Sold<br>or Disposed<span v-html="showSort('total_cost_sold')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_price_sold' }]" @click="setSort('total_price_sold')">Retail Sold<br>or Disposed<span v-html="showSort('total_price_sold')"></span></label>
                    <label :class="['right', { sortable: department_id }, { sortcurrent: department_id && filters.sort_column == 'total_profit_sold' }]" @click="setSort('total_profit_sold')"><br>Profit<span v-html="showSort('total_profit_sold')"></span></label>
                    <label class="right"><br>Margin</label>
                    <label class="center"><br>YTD</label>
                </div>
                <div class="report-row" v-for="(data, index) in list" :key="index">
                    <div>
                        <span class="row-title">{{ data.description }}</span>
                        <div class="two-columns row-controls">
                            <div v-if="data.upc">
                                <label>UPC</label><span class="caption">{{ data.upc }}</span>
                            </div>
                            <div>
                                <div v-if="data.total_on_hand > 0" class="button inventory" @click="getListItems(data)"><i class="fas fa-list"></i></div>
                                <div v-else class="button disabled"><i class="fas fa-list"></i></div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label class="full bordered">POS</label><br>
                        <label class="full bordered">Web</label><br>
                        <label class="full bordered">Logbooks</label><br>
                        <label class="full">Total</label>
                    </div>

                    <div>
                        <span class="numbers full bordered">{{ data.on_hand_store }}</span><br>
                        <span class="numbers full bordered">&nbsp;</span><br>
                        <span class="numbers full bordered">{{ data.on_hand_logbooks || 0 }}</span>
                        <span class="numbers full">{{ data.total_on_hand }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ formatCurrency(data.cost_on_hand_store) }}</span><br>
                        <span class="numbers full bordered">&nbsp;</span><br>
                        <span class="numbers full bordered">{{ formatCurrency(data.cost_on_hand_logbooks) }}</span><br>
                        <span class="numbers full">{{ formatCurrency(data.total_cost_on_hand) }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ formatCurrency(data.price_on_hand_store) }}</span><br>
                        <span class="numbers full bordered">&nbsp;</span><br>
                        <span class="numbers full bordered">{{ formatCurrency(data.price_on_hand_logbooks) }}</span><br>
                        <span class="numbers full">{{ formatCurrency(data.total_price_on_hand) }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ formatCurrency(data.profit_on_hand_store) }}</span><br>
                        <span class="numbers full bordered">&nbsp;</span><br>
                        <span class="numbers full bordered">{{ formatCurrency(data.profit_on_hand_logbooks) }}</span><br>
                        <span class="numbers full">{{ formatCurrency(data.total_profit_on_hand) }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ data.margin_on_hand_store || '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">&nbsp;</span><br>
                        <span class="numbers full bordered">{{ data.margin_on_hand_logbooks || '&nbsp;'  }}</span><br>
                        <span class="numbers full">{{ data.total_margin_on_hand || '&nbsp;'  }}</span>
                    </div>

                    <div class="dark-shaded"></div>

                    <div>
                        <span class="numbers full bordered">{{ data.sold_pos || '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.sold_online || '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.disposed_logbooks || '&nbsp;' }}</span>
                        <span class="numbers full">{{ data.total_sold }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ data.cost_sold_pos ? formatCurrency(data.cost_sold_pos) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.cost_sold_online ? formatCurrency(data.cost_sold_online) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.cost_disposed_logbooks ? formatCurrency(data.cost_disposed_logbooks) : '&nbsp;' }}</span><br>
                        <span class="numbers full">{{ data.total_cost_sold > 0 ? formatCurrency(data.total_cost_sold) : '&nbsp;' }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ data.price_sold_pos ? formatCurrency(data.price_sold_pos) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.price_sold_online ? formatCurrency(data.price_sold_online) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.price_disposed_logbooks ? formatCurrency(data.price_disposed_logbooks) : '&nbsp;' }}</span><br>
                        <span class="numbers full">{{ data.total_price_sold > 0 ? formatCurrency(data.total_price_sold) : '&nbsp;' }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ data.profit_sold_pos > 0 ? formatCurrency(data.profit_sold_pos) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.profit_sold_online > 0 ? formatCurrency(data.profit_sold_online) : '&nbsp;' }}</span><br>
                        <span class="numbers full bordered">{{ data.profit_disposed_logbooks > 0 ? formatCurrency(data.profit_disposed_logbooks) : '&nbsp;' }}</span><br>
                        <span class="numbers full">{{ data.total_profit_sold > 0 ? formatCurrency(data.total_profit_sold) : '&nbsp;' }}</span>
                    </div>
                    <div>
                        <span class="numbers full bordered">{{ data.margin_sold_pos || '&nbsp;'  }}</span><br>
                        <span class="numbers full bordered">{{ data.margin_sold_online || '&nbsp;'  }}</span><br>
                        <span class="numbers full bordered">{{ data.margin_disposed_logbooks || '&nbsp;' }}</span><br>
                        <span class="numbers full">{{ data.total_margin_sold }}</span>
                    </div>

                    <div class="shaded" style="margin: 0 0 0 0.25em;">
                        <br>
                        <label class="center">GMROI</label><div style="padding: 0; text-align: center;">{{ data.gmroi }}</div>
                    </div>

                </div>

                <pagination v-if="section == 'items'" :paging="paging" @updatePage="goToPage"></pagination>

            </div>
            <!-- <div v-if="!loading && allListsEmpty()" class="empty-list">
                No data found for {{ department_name || 'report period' }}
            </div> -->
        </template>

        <compare-dates :active="showCompare" :section="'inventory'" @cancel="showCompare = false" />

    </div>
</template>
<script>
export default {
    name: 'inventory',
    props: ['department_id'],
    data() {
        return {
            loading: true,
            message: '',
            filters: {
                section: 'inventory',
                start_date: '',
                end_date: '',
                department_id: '',
                manufacturer_id: '',
                manufacturer_name: '',
                search_text: '',
                location_id: '',
                location_name: '',
                filter_by_list: '',
                page: 1,
                inventory_only: true,
                sort_column: 'total_price_on_hand',
                sort_descending: true
            },
            totals: {},
            lists: {
                firearms: [],
                non_firearms: [],
                items: []
            },
            paging: {},
            department_name: '',
            manufacturerSearchList: [],
            manufactuerSearchMessage: '',
            itemSearchList: [],
            itemSearchMessage: '',
            dealerSearchList: [],
            dealerSearchMessage: '',
            showCompare: false
        }
    },
    created() {
        this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
        this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
        this.minFilterDate = this.filters.start_date.split('-').shift() + '-01-01'
        this.maxFilterDate = this.filters.end_date.split('-').shift() + '-12-31'
        // any filters previously stored for this section?
        if (this.getFilters.section == 'inventory') {
            this.filters = this.getFilters // filters were stored last time we hit fetchDataItems()
        }
        if (this.department_id) {
            this.filters.department_id = this.department_id
            this.fetchDataItems(true)
        } else {
            this.filters.department_id = ''
            this.fetchData()
        }
    },
    computed: {
        showPagination() {
            return this.department_id && this.lists.items.length > 0 && this.paging.total_pages > 1
        },
        showDealerFilter() {
            return false; // this.getUser && this.getUser.user_type_code && this.getUser.user_type_code.indexOf('ADMIN') > -1
        }
    },
    methods: {
		fetchData() {
            this.totals = {}
            this.loading = true
            this.message = 'Loading'
            this.apiCall({ destination: 'get_report_inventory', data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    this.lists.firearms = obj.firearms
                    this.lists.non_firearms = obj.non_firearms
                    this.totals = obj.totals
                    this.message = ''
                    this.loading = false
                    window.scrollTo(0,0);
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterDates() {
            this.$router.push({ path: this.$route.path, query: { start: this.filters.start_date, end: this.filters.end_date } })
        },
        sectionTitle(section) {
            switch (section) {
                case 'firearms':
                    return 'Firearms'
                case 'non_firearms':
                    return 'Accessories'
                case 'items':
                    return this.department_name
            }
        },
        getListItems(data) {
            if (data.department_id) {
                //this.storeTotals({ on_hand_cost: data.total_cost_on_hand, on_hand_price: data.total_price_on_hand, on_hand_margin: data.total_margin })
                this.$router.push({path: '/inventory/' + data.department_id, query: this.$route.query})
            } else if (data.product_id) {
                this.$router.push({path: '/item/inventory/' + data.product_id, query: this.$route.query})
            }
        },
        goToPage(page) {
			this.paging.current_page = page
			this.setFilter('page')
		},
        showSort(column) {
            // don't return anything if we're not filtered
            // TODO: filtered also means manufacturer, search term, or dealer
            if (!this.filters.department_id) {
                return
            }
            if (this.filters.sort_column == column) {
                if (this.filters.sort_descending) {
                    return "<i class='fas fa-caret-down'></i>"
                } else {
                    return "<i class='fas fa-caret-up'></i>"
                }
            } else {
                return "&nbsp;"
            }
        },
        setSort(which) {
            //only sort if drilled down on a department and we're not already loading
            if (!this.department_id) { return }
            if (this.loading) { return }
            if (this.filters.sort_column == which) {
                this.filters.sort_descending = !this.filters.sort_descending
            } else {
                this.filters.sort_column = which
            }
             this.fetchDataItems(false)
        },
        setFilter(which, action) {
            switch (which) {
                case 'page':
                    this.filters.page = this.paging.current_page
                    this.fetchDataItems(true)
                    return false;
                case 'manufacturer':
                    return false
                    this.manufacturerSearchList = []
                    this.manufactuerSearchMessage = ''
                    if (action == 'clear') {
                        this.filters.manufacturer_name = ''
                        this.filters.manufacturer_id = ''
                        this.filter_by_list = ''
                        if (this.filters.search_text) {
                            this.fetchDataItems(true)
                        } else {
                            this.fetchData()
                        }
                    } else {
                        this.searchFilter('manufacturer')
                    }
                    return false
                case 'search_text':
                    if (this.filters.search_text) {
                        this.fetchDataItems(true)
                    } else {
                        this.fetchData()
                    }
                    return false
                case 'location':
                    this.dealerSearchMessage = 'Dealer search coming soon'
                    setTimeout(() => { this.dealerSearchMessage = '' }, 2000)
                    return false
            }
            if (this.department_id) {
                this.filters.department_id = this.department_id
                this.fetchDataItems(true)
            } else {
                this.fetchData()
            }
        },
        clearFilters() {
            this.filters = {
                section: 'inventory',
                start_date: '',
                end_date: '',
                department_id: '',
                manufacturer_id: '',
                manufacturer_name: '',
                search_text: '',
                location_id: '',
                location_name: '',
                filter_by_list: '',
                page: 1,
                inventory_only: true,
                sort_column: 'on_hand_price'
            }
            this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
            this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
            this.department_name = ''
            this.message = ''
            if (this.$route.path != '/inventory') {
                // pushing route will clear id
                this.$router.push({path: '/inventory', query: this.$route.query})
            } else {
                // but if base route already, reload data
                this.fetchData()
            }
        },
        searchFilter(which) {
            this.filters.filter_by_list = which
            this.apiCall({ destination: 'get_filter_list', data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    if (obj.filter_list.length == 1) {
                        this.filters.manufacturer_name = obj.filter_list[0]['description']
                        this.filters.manufacturer_id = obj.filter_list[0]['manufacturer_id']
                        this.fetchDataItems(true)
                    } else if (obj.filter_list.length > 1) {
                        this.manufacturerSearchList = obj.filter_list
                    } else {
                        this.manufactuerSearchMessage = 'Manufacturer not found'
                    }
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterFromSearch(data) {
            if (this.filters.filter_by_list == 'manufacturer') {
                this.manufacturerSearchList = []
                this.manufactuerSearchMessage = ''
                this.filters.manufacturer_name = data.description
                this.filters.manufacturer_id = data['manufacturer_id']
            }
            if (this.filters.filter_by_list == 'location') {
                this.dealerSearchList = []
                this.dealerSearchMessage =  ''
                this.filters.location_name = data.description
                this.filters.location_id = data['location_id']
            }
            this.fetchDataItems(true)
        },
        fetchDataItems(resetTotals) {
            this.totals = {}
            this.loading = true
            this.message = 'Loading'
            this.apiCall({ destination: 'get_report_inventory_items', data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    this.loading = false
                    this.storeFilters(this.filters) // store current filter set
                    this.department_name = obj.department_name
                    this.lists.firearms = []
                    this.lists.non_firearms = []
                    if (obj.items.length > 0) {
                        this.lists.items = obj.items
                        if (resetTotals) {
                            this.totals = this.getTotals
                        }
                        this.paging = obj.paging
                        this.message = ''
                        window.scrollTo(0,0);
                    } else {
                        this.message = 'Items not found'
                        setTimeout(() => { this.message = '' }, 2000)
                    }
                } else {
                    this.loading = false
                    this.message = obj.message
                    setTimeout(() => { this.message = '' }, 2000)
                }
            })
        },
        allListsEmpty() {
            return this.lists.firearms.length == 0 && this.lists.non_firearms.length == 0 && this.lists.items.length == 0
        },
        getItem(productId) {
            this.$router.push('/item/' + productId)
        }
    }
}
</script>
<style lang="scss" scoped>
.report-row {
    grid-template-columns: 2.5fr 0.5fr 5em 1fr 1.25fr 1fr 0.75fr 3px 0.75fr 1fr 1fr 1fr 0.75fr 0.66fr;
}
.dark-shaded { border-right: 1px solid #ccc; padding: 0 0 0 2px; }
.row-controls {
    grid-template-columns: 3fr 1fr;
    label { width: auto; padding: 0 0.5em 0 0.5em; }
}
</style>
